/**
 * Adapted from PlayCanvas Engine's gsplat-info.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type {
  GSplatGraphicsAdapter,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import type {
  GSplatParameter,
  GSplatPlacement,
  GSplatPlacementResource,
  GSplatPlacementScalarFilter,
} from '@/plugins/gsplat/unified/GSplatPlacement'
import type { GSplatOctreeNode } from '@/plugins/gsplat/unified/octree/GSplatOctree'
import type { GSplatOctreeNodeInfo } from '@/plugins/gsplat/unified/octree/GSplatOctreeInstance'
import type { AssetId } from '@/shared/types/core'
import type { CompiledGSplatWorkBufferModifier } from '@/shared/types/gsplat'
import { GSPLAT_WORKBUFFER_UPDATE_ALWAYS } from '@/shared/types/gsplat'

const UINT16_MAX = 0xffff
const UINT32_MAX = 0xffffffff

export interface GSplatInfoResource extends GSplatPlacementResource {
  readonly id: number
  /** 格式原生 CPU 椭球读取器；radii 已是实际半径。 */
  readSplatShape?(
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ): boolean
  /** 格式原生 CPU 基色读取器；rgb 等价 shader readBaseColor。 */
  readSplatColor?(index: number, color: THREE.Vector4): boolean
  readonly numSplats: number
  readonly centersVersion: number
  readonly centers: Float32Array | null
  readonly aabb: THREE.Box3
  readonly format: GSplatFormat
  readonly maxSplats?: number
  readonly data?: { readonly shBands?: number }
  readonly gsplatData?: { readonly shBands?: number }
  incRefCount?(): void
  decRefCount?(): void
}

export interface GSplatInfoUpdatePlan {
  readonly id: number
  readonly baselineRevision: number
  readonly changed: boolean
  readonly transformChanged: boolean
  readonly previousWorldMatrix: readonly number[]
  readonly worldMatrix: readonly number[]
  readonly previousDirtyVersion: number
  readonly dirtyVersion: number
  readonly previousFormatVersion: number
  readonly formatVersion: number
}

/** Render-ready WorldState 中的 Placement snapshot。 */
export class GSplatInfo {
  readonly resource: GSplatInfoResource
  readonly node: THREE.Object3D
  readonly lodIndex: number
  readonly placementId: number
  readonly assetId: AssetId | undefined
  readonly allocId: number
  readonly parentPlacementId: number
  readonly numSplats: number
  readonly aabb: THREE.Box3
  readonly parameters: Map<string, GSplatParameter> | null
  readonly octreeNodes: readonly GSplatOctreeNode[] | null
  readonly nodeInfos: readonly GSplatOctreeNodeInfo[] | null
  readonly intervals: number[] = []
  readonly intervalOffsets: number[] = []
  readonly intervalAllocIds: number[] = []
  readonly intervalNodeIndices: number[] = []
  readonly previousWorldTransform = new THREE.Matrix4()
  activeSplats = 0
  subDrawTexture: GSplatTextureLeaf | null = null
  subDrawCount = 0
  numBoundsEntries = 0
  boundsBaseIndex = 0
  colorAccumulatedTranslation = 0

  readonly getWorkBufferModifier: () => CompiledGSplatWorkBufferModifier | null
  readonly getScalarFilter: () => GSplatPlacementScalarFilter | null
  readonly getIsEnvironment: () => boolean
  readonly getInstanceStreams: () => GSplatStreams | null
  private readonly getDirtySource: () => GSplatPlacement
  private readonly adapter: GSplatGraphicsAdapter
  private readonly issuedUpdatePlans = new WeakSet<GSplatInfoUpdatePlan>()
  private readonly committedUpdatePlans = new WeakSet<GSplatInfoUpdatePlan>()
  private lastDirtyVersion = -1
  private lastFormatVersion = -1
  private nextUpdatePlanId = 1
  private baselineRevision = 0
  private destroyed = false

  constructor(
    adapter: GSplatGraphicsAdapter,
    resource: GSplatInfoResource,
    placement: GSplatPlacement,
    octreeNodes: readonly GSplatOctreeNode[] | null = null,
    nodeInfos: readonly GSplatOctreeNodeInfo[] | null = null
  ) {
    if (!resource || !placement) throw new Error('GSplatInfo 要求 Resource 与 Placement')
    if (resource.format.graphicsAdapter !== adapter) {
      throw new Error('GSplatInfo Resource format 与 adapter 不属于同一 graphics device')
    }
    if ((octreeNodes === null) !== (nodeInfos === null)) {
      throw new Error('GSplatInfo Octree nodes/nodeInfos 必须同时提供')
    }
    if (octreeNodes && nodeInfos && octreeNodes.length !== nodeInfos.length) {
      throw new Error('GSplatInfo Octree nodes/nodeInfos 长度不匹配')
    }

    this.adapter = adapter
    this.resource = resource
    this.node = placement.node
    this.lodIndex = placement.lodIndex
    this.placementId = placement.id
    this.assetId = placement.assetId
    this.allocId = placement.allocId
    this.parentPlacementId =
      octreeNodes && placement.parentPlacement
        ? placement.parentPlacement.allocId
        : placement.allocId
    this.numSplats = resource.numSplats
    this.aabb = placement.aabb.clone()
    this.parameters = placement.parameters
    this.octreeNodes = octreeNodes
    this.nodeInfos = nodeInfos
    this.getWorkBufferModifier = () => placement.workBufferModifier
    this.getScalarFilter = () => placement.scalarFilter
    this.getIsEnvironment = () => placement.isEnvironment
    this.getInstanceStreams = () => placement.streams
    this.getDirtySource = () => placement.parentPlacement ?? placement
    this.updateIntervals(placement.intervals)
  }

  /** 环境（skybox）placement 标记；scalar 过滤与直方图据此豁免。 */
  get isEnvironment(): boolean {
    return this.getIsEnvironment()
  }

  setLayout(intervalOffsets: readonly number[]): void {
    this.assertActive()
    const expected = Math.max(1, this.intervals.length / 2)
    if (this.activeSplats > 0 && intervalOffsets.length !== expected) {
      throw new Error(
        `GSplatInfo layout offset 数量不匹配：actual=${intervalOffsets.length}, expected=${expected}`
      )
    }
    for (const offset of intervalOffsets) {
      if (!Number.isSafeInteger(offset) || offset < 0) {
        throw new Error(`非法 GSplatInfo layout offset: ${offset}`)
      }
    }
    this.intervalOffsets.length = 0
    this.intervalOffsets.push(...intervalOffsets)
    this.subDrawTexture?.dispose()
    this.subDrawTexture = null
    this.subDrawCount = 0
  }

  ensureSubDrawTexture(textureWidth: number): void {
    this.assertActive()
    if (!this.subDrawTexture && this.activeSplats > 0) this.updateSubDraws(textureWidth)
  }

  appendSubDraws(
    target: Uint32Array,
    subDrawCount: number,
    sourceBase: number,
    size: number,
    targetOffset: number,
    textureWidth: number
  ): number {
    validateUnsigned('sourceBase', sourceBase, UINT32_MAX)
    validateUnsigned('size', size, UINT32_MAX)
    validateUnsigned('targetOffset', targetOffset, UINT32_MAX)
    validateUnsigned('subDrawCount', subDrawCount, UINT32_MAX)
    if (!Number.isSafeInteger(textureWidth) || textureWidth < 1) {
      throw new Error(`非法 GSplat sub-draw textureWidth: ${textureWidth}`)
    }
    if (size === 0) return subDrawCount
    if (sourceBase + size > UINT32_MAX) throw new Error('GSplat sub-draw source range 超出 uint32')
    const capacity = textureWidth * textureWidth
    if (!Number.isSafeInteger(capacity) || targetOffset + size > capacity) {
      throw new Error('GSplat sub-draw target range 超出 WorkBuffer capacity')
    }

    let remaining = size
    let row = Math.floor(targetOffset / textureWidth)
    const col = targetOffset % textureWidth
    if (col > 0) {
      const count = Math.min(remaining, textureWidth - col)
      this.writeSubDraw(target, subDrawCount++, row, 1, col, col + count, sourceBase)
      sourceBase += count
      remaining -= count
      row++
    }

    const fullRows = Math.floor(remaining / textureWidth)
    if (fullRows > 0) {
      this.writeSubDraw(target, subDrawCount++, row, fullRows, 0, textureWidth, sourceBase)
      sourceBase += fullRows * textureWidth
      remaining -= fullRows * textureWidth
      row += fullRows
    }

    if (remaining > 0) {
      this.writeSubDraw(target, subDrawCount++, row, 1, 0, remaining, sourceBase)
    }
    return subDrawCount
  }

  update(): GSplatInfoUpdatePlan {
    this.assertActive()
    this.node.updateWorldMatrix(true, false)
    const worldMatrix = Object.freeze([...this.node.matrixWorld.elements])
    const transformChanged = !matrixElementsEqual(this.previousWorldTransform.elements, worldMatrix)
    const dirtySource = this.getDirtySource()
    const dirtyVersion = dirtySource.dirtyVersion
    const formatVersion = this.resource.format.extraStreamsVersion
    const changed =
      transformChanged ||
      this.lastDirtyVersion !== dirtyVersion ||
      dirtySource.workBufferUpdate === GSPLAT_WORKBUFFER_UPDATE_ALWAYS ||
      this.lastFormatVersion !== formatVersion
    const plan = Object.freeze({
      id: this.nextUpdatePlanId++,
      baselineRevision: this.baselineRevision,
      changed,
      transformChanged,
      previousWorldMatrix: Object.freeze([...this.previousWorldTransform.elements]),
      worldMatrix,
      previousDirtyVersion: this.lastDirtyVersion,
      dirtyVersion,
      previousFormatVersion: this.lastFormatVersion,
      formatVersion,
    })
    this.issuedUpdatePlans.add(plan)
    return plan
  }

  commitUpdateBaseline(plan: GSplatInfoUpdatePlan): void {
    this.assertActive()
    if (!this.issuedUpdatePlans.has(plan)) {
      throw new Error('GSplatInfo update plan 不属于当前 snapshot 或已提交')
    }
    if (plan.baselineRevision !== this.baselineRevision) {
      throw new Error('GSplatInfo update plan 已过期')
    }
    this.node.updateWorldMatrix(true, false)
    const dirtySource = this.getDirtySource()
    if (
      dirtySource.dirtyVersion !== plan.dirtyVersion ||
      this.resource.format.extraStreamsVersion !== plan.formatVersion ||
      !matrixElementsEqual(this.node.matrixWorld.elements, plan.worldMatrix)
    ) {
      throw new Error('GSplatInfo update plan source baseline 已失效')
    }
    this.previousWorldTransform.fromArray(plan.worldMatrix)
    this.lastDirtyVersion = plan.dirtyVersion
    this.lastFormatVersion = plan.formatVersion
    this.issuedUpdatePlans.delete(plan)
    this.committedUpdatePlans.add(plan)
    this.baselineRevision++
  }

  rollbackUpdateBaseline(plan: GSplatInfoUpdatePlan): void {
    this.assertActive()
    if (!this.committedUpdatePlans.has(plan)) return
    if (this.baselineRevision !== plan.baselineRevision + 1) {
      throw new Error('GSplatInfo update plan 已被后续提交覆盖，不能回滚')
    }
    this.previousWorldTransform.fromArray(plan.previousWorldMatrix)
    this.lastDirtyVersion = plan.previousDirtyVersion
    this.lastFormatVersion = plan.previousFormatVersion
    this.committedUpdatePlans.delete(plan)
    this.baselineRevision--
  }

  writeBoundsSpheres(data: Float32Array, offset: number): void {
    this.assertActive()
    validateUnsigned('bounds offset', offset, data.length)
    const required = this.numBoundsEntries * 4
    if (offset + required > data.length) throw new Error('GSplat bounds target capacity 不足')
    if (this.octreeNodes) {
      for (const node of this.octreeNodes) {
        const sphere = node.boundingSphere
        data[offset++] = sphere.x
        data[offset++] = sphere.y
        data[offset++] = sphere.z
        data[offset++] = sphere.w
      }
      return
    }
    const center = this.resource.aabb.getCenter(new THREE.Vector3())
    const halfExtents = this.resource.aabb.getSize(new THREE.Vector3()).multiplyScalar(0.5)
    data[offset++] = center.x
    data[offset++] = center.y
    data[offset++] = center.z
    data[offset] = halfExtents.length()
  }

  get hasSphericalHarmonics(): boolean {
    return (this.resource.data?.shBands ?? this.resource.gsplatData?.shBands ?? 0) > 0
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.intervals.length = 0
    this.intervalOffsets.length = 0
    this.intervalAllocIds.length = 0
    this.intervalNodeIndices.length = 0
    this.subDrawTexture?.dispose()
    this.subDrawTexture = null
    this.subDrawCount = 0
  }

  dispose(): void {
    this.destroy()
  }

  private updateIntervals(
    intervals: ReadonlyMap<number, { readonly start: number; readonly end: number }>
  ): void {
    this.activeSplats = this.resource.numSplats
    if (intervals.size > 0) {
      let totalCount = 0
      for (const [nodeIndex, interval] of intervals) {
        if (!Number.isSafeInteger(nodeIndex) || nodeIndex < 0) {
          throw new Error(`非法 GSplat interval nodeIndex: ${nodeIndex}`)
        }
        if (
          !Number.isSafeInteger(interval.start) ||
          !Number.isSafeInteger(interval.end) ||
          interval.start < 0 ||
          interval.end < interval.start ||
          interval.end >= this.resource.numSplats
        ) {
          throw new Error(
            `非法 GSplat interval: node=${nodeIndex}, [${interval.start}, ${interval.end}]`
          )
        }
        this.intervals.push(interval.start, interval.end + 1)
        totalCount += interval.end - interval.start + 1
        if (this.nodeInfos) {
          const nodeInfo = this.nodeInfos[nodeIndex]
          if (!nodeInfo || !this.octreeNodes?.[nodeIndex]) {
            throw new Error(`GSplat interval nodeIndex 越界: ${nodeIndex}`)
          }
          this.intervalAllocIds.push(nodeInfo.allocId)
          this.intervalNodeIndices.push(nodeIndex)
        }
      }
      if (this.octreeNodes) {
        this.activeSplats = totalCount
        this.numBoundsEntries = this.octreeNodes.length
      } else if (totalCount === this.numSplats) {
        this.intervals.length = 0
      } else {
        this.activeSplats = totalCount
      }
    } else {
      this.numBoundsEntries = 1
      this.intervalAllocIds.push(this.allocId)
      if (this.resource.maxSplats && this.activeSplats < this.resource.maxSplats) {
        this.intervals.push(0, this.activeSplats)
      }
    }
  }

  private updateSubDraws(textureWidth: number): void {
    if (!Number.isSafeInteger(textureWidth) || textureWidth < 1) {
      throw new Error(`非法 GSplat sub-draw textureWidth: ${textureWidth}`)
    }
    const intervals = this.intervals.length > 0 ? this.intervals : [0, this.activeSplats]
    const numIntervals = intervals.length / 2
    if (this.intervalOffsets.length !== numIntervals) {
      throw new Error('GSplat sub-draw interval/layout 数量不匹配')
    }
    const data = new Uint32Array(numIntervals * 3 * 4)
    let count = 0
    for (let index = 0; index < numIntervals; index++) {
      count = this.appendSubDraws(
        data,
        count,
        intervals[index * 2],
        intervals[index * 2 + 1] - intervals[index * 2],
        this.intervalOffsets[index],
        textureWidth
      )
    }
    if (count === 0) return
    const dimensions = this.adapter.calculateTextureDimensions(count)
    if (
      dimensions.width < 1 ||
      dimensions.height < 1 ||
      dimensions.width > this.adapter.limits.maxTextureDimension2D ||
      dimensions.height > this.adapter.limits.maxTextureDimension2D ||
      dimensions.width * dimensions.height < count
    ) {
      throw new Error('GSplat sub-draw texture dimensions 非法')
    }
    const descriptor = {
      label: `gsplat-sub-draw:${this.placementId}`,
      format: 'rgba32uint' as const,
      width: dimensions.width,
      height: dimensions.height,
      usages: Object.freeze(['sampled', 'copy-dst'] as const),
    }
    this.adapter.validateTextureDescriptor(descriptor)
    const staged = this.adapter.createTexture(descriptor)
    try {
      // Texture upload is an exact storage replacement. Preserve PlayCanvas' zero-filled padding
      // when the square-ish texture capacity is larger than the used sub-draw count.
      const upload = new Uint32Array(dimensions.width * dimensions.height * 4)
      upload.set(data.subarray(0, count * 4))
      this.adapter.updateTexture(staged, upload)
      this.subDrawTexture = staged
      this.subDrawCount = count
    } catch (error) {
      staged.dispose()
      throw error
    }
  }

  private writeSubDraw(
    target: Uint32Array,
    index: number,
    rowStart: number,
    rowCount: number,
    colStart: number,
    colEnd: number,
    sourceBase: number
  ): void {
    if (index * 4 + 3 >= target.length) throw new Error('GSplat sub-draw typed-array capacity 不足')
    validateUnsigned('rowStart', rowStart, UINT16_MAX)
    validateUnsigned('rowCount', rowCount, UINT16_MAX)
    validateUnsigned('colStart', colStart, UINT32_MAX)
    validateUnsigned('colEnd', colEnd, UINT32_MAX)
    validateUnsigned('sourceBase', sourceBase, UINT32_MAX)
    target[index * 4] = rowStart | (rowCount << 16)
    target[index * 4 + 1] = colStart
    target[index * 4 + 2] = colEnd
    target[index * 4 + 3] = sourceBase
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatInfo 已销毁')
  }
}

function validateUnsigned(label: string, value: number, limit: number): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > limit) {
    throw new Error(`非法 GSplat ${label}: ${value}`)
  }
}

function matrixElementsEqual(left: ArrayLike<number>, right: ArrayLike<number>): boolean {
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index++) {
    if (left[index] !== right[index]) return false
  }
  return true
}
